// Who may be DELIVERED a gallery / video. Mirrors api/Models/Audience.cs and
// AudienceGate's storage literals exactly. This is orthogonal to moderation
// (pending / live / rejected): a Friends-only gallery is still reviewed by
// staff before it goes live, and staff can always see it. Say WATCHERS, never
// "followers": Watching / watcherCount is this product's word for that graph.

export type Audience = 'Public' | 'Friends' | 'Watchers';

export const AUDIENCES: readonly Audience[] = ['Public', 'Friends', 'Watchers'] as const;

/** Short label used on chips / badges / eyebrows. */
export const AUDIENCE_LABEL: Record<Audience, string> = {
  Public: 'Anyone',
  Friends: 'Friends only',
  Watchers: 'Watchers only',
};

/** One-line hint under each picker option. */
export const AUDIENCE_HINT: Record<Audience, string> = {
  Public: 'Anyone on JuicyStarz, including guests and search engines.',
  Friends: 'Only your accepted friends. A pending request does not count.',
  Watchers: 'Anyone watching you.',
};

/**
 * The disclosure that must sit next to every audience control. "Private"
 * without this reads as secret from the platform, and it is not: private
 * content is still scanned, still reviewed, and always visible to staff.
 */
export const AUDIENCE_DISCLOSURE =
  'Friends-only and Watchers-only content is still reviewed by JuicyStarz staff and goes live only after review, like everything else.';

/** Coerce anything the API or a form hands us into a valid Audience. Unknown → Public. */
export function normalizeAudience(v: unknown): Audience {
  if (typeof v !== 'string') return 'Public';
  const s = v.trim().toLowerCase();
  if (s === 'friends') return 'Friends';
  if (s === 'watchers') return 'Watchers';
  return 'Public';
}

export function isPrivateAudience(v: unknown): boolean {
  return normalizeAudience(v) !== 'Public';
}
